import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { 
  MessageSquare, CheckCircle, AlertTriangle, Clock, TrendingUp,
  Phone, MessageCircle, Loader2, Calendar, RotateCcw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const todayStr = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const ComplaintsDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(''); // '' = all time
  const user = authService.getCurrentUser();
  const navigate = useNavigate();

  const fetchStats = useCallback(async (dateValue) => {
    setLoading(true);
    try {
      const response = await api.getComplaintStats(dateValue || undefined);
      const data = response.data;

      let bySourceArray = [];
      if (data.by_source) {
        if (Array.isArray(data.by_source)) {
          bySourceArray = data.by_source;
        } else {
          bySourceArray = Object.entries(data.by_source).map(([key, value]) => ({ _id: key, count: value }));
        }
      }

      let byProblemTypeArray = [];
      if (data.by_problem_type) {
        byProblemTypeArray = data.by_problem_type.map(item => ({
          _id: item.type || item._id,
          count: item.count
        }));
      }

      setStats({
        total_complaints: data.total,
        open_complaints: data.by_status?.open || 0,
        in_progress: data.by_status?.in_progress || 0,
        solved_complaints: data.by_status?.solved || 0,
        escalated_complaints: data.by_status?.escalated || 0,
        solve_rate: data.resolution_rate || 0,
        by_source: bySourceArray,
        by_problem_type: byProblemTypeArray,
        scope: data.scope,
      });
    } catch (error) {
      toast.error('Failed to fetch complaint statistics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats(selectedDate);
  }, [selectedDate, fetchStats]);

  const scopeLabel = selectedDate
    ? new Date(selectedDate + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })
    : 'All Time';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color, bgColor, onClick }) => (
    <div 
      data-testid={`complaint-stat-${title.toLowerCase().replace(/\s+/g, '-')}`} 
      onClick={onClick}
      className={`bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all p-6 ${onClick ? 'cursor-pointer hover:scale-105 active:scale-95' : ''}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 font-body mb-2">{title}</p>
          <p className="text-3xl font-bold font-headings" style={{ color }}>{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${bgColor}`}>
          <Icon size={24} style={{ color }} />
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="complaints-dashboard" className="max-w-[1600px] mx-auto space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-bold font-headings uppercase tracking-tight text-slate-900 mb-2">
            Complaints Dashboard
          </h1>
          <p className="text-slate-600 font-body">
            Overview of complaint management performance — <span className="font-semibold text-slate-900">{scopeLabel}</span>.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2 shadow-sm">
          <Calendar size={16} className="text-slate-500" />
          <input
            type="date"
            value={selectedDate}
            max={todayStr()}
            onChange={(e) => setSelectedDate(e.target.value)}
            data-testid="dashboard-date-picker"
            className="text-sm bg-transparent focus:outline-none"
          />
          {selectedDate && (
            <button
              onClick={() => setSelectedDate('')}
              data-testid="dashboard-date-reset"
              className="ml-1 p-1 text-slate-400 hover:text-slate-700 rounded"
              title="Reset to all time"
            >
              <RotateCcw size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Main KPI - Solve Rate */}
      <div className="bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl shadow-lg p-8 text-white">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium uppercase tracking-wider text-orange-100 mb-2 flex items-center gap-2">
              <TrendingUp size={16} />
              Resolution Rate · {scopeLabel}
            </p>
            <div className="flex items-baseline gap-4">
              <p className="text-6xl font-bold font-headings" data-testid="resolution-rate-value">{stats?.solve_rate || 0}%</p>
              <span className="text-orange-100 text-sm">
                {stats?.solved_complaints || 0} solved out of {stats?.total_complaints || 0}
              </span>
            </div>
          </div>
          <div className="p-4 bg-white/10 rounded-xl backdrop-blur-sm">
            <CheckCircle size={48} className="text-white" />
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Total Complaints" 
          value={stats?.total_complaints || 0} 
          icon={MessageSquare} 
          color="#0F172A" 
          bgColor="bg-slate-100"
          onClick={() => navigate('/complaints/list')}
        />
        <StatCard 
          title="Open" 
          value={stats?.open_complaints || 0} 
          icon={Clock} 
          color="#3B82F6" 
          bgColor="bg-blue-50"
          onClick={() => navigate('/complaints/list', { state: { statusFilter: 'open' } })}
        />
        <StatCard 
          title="In Progress" 
          value={stats?.in_progress || 0} 
          icon={MessageCircle} 
          color="#F59E0B" 
          bgColor="bg-amber-50"
          onClick={() => navigate('/complaints/list', { state: { statusFilter: 'in_progress' } })}
        />
        <StatCard 
          title="Escalated" 
          value={stats?.escalated_complaints || 0} 
          icon={AlertTriangle} 
          color="#EF4444" 
          bgColor="bg-red-50"
          onClick={() => navigate('/complaints/list', { state: { escalated: true } })}
        />
      </div>

      {/* Stats by Source and Problem Type */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Source */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold font-headings text-slate-900 mb-4 flex items-center gap-2">
            <Phone size={20} />
            Complaints by Source
          </h2>
          <div className="space-y-3">
            {stats?.by_source?.map((item) => (
              <div key={item._id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  {item._id === 'calling' ? (
                    <Phone size={18} className="text-blue-600" />
                  ) : (
                    <MessageCircle size={18} className="text-green-600" />
                  )}
                  <span className="font-medium text-slate-700 capitalize">{item._id || 'Unknown'}</span>
                </div>
                <span className="font-bold text-slate-900">{item.count}</span>
              </div>
            ))}
            {(!stats?.by_source || stats.by_source.length === 0) && (
              <p className="text-slate-500 text-center py-4">No data yet</p>
            )}
          </div>
        </div>

        {/* By Problem Type */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold font-headings text-slate-900 mb-4 flex items-center gap-2">
            <AlertTriangle size={20} />
            Complaints by Problem Type
          </h2>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {stats?.by_problem_type?.map((item, index) => (
              <div key={item._id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="font-medium text-slate-700">{item._id || 'Unknown'}</span>
                <span className="font-bold text-slate-900">{item.count}</span>
              </div>
            ))}
            {(!stats?.by_problem_type || stats.by_problem_type.length === 0) && (
              <p className="text-slate-500 text-center py-4">No data yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h2 className="text-2xl font-semibold font-headings text-slate-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/complaints/new')}
            data-testid="quick-action-new-complaint"
            className="flex items-center gap-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-left"
          >
            <MessageSquare size={24} className="text-orange-600" />
            <div>
              <p className="font-medium font-body text-slate-900">New Complaint</p>
              <p className="text-xs text-slate-500 font-body">Register a new complaint</p>
            </div>
          </button>
          <button
            onClick={() => navigate('/complaints/list')}
            data-testid="quick-action-view-complaints"
            className="flex items-center gap-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-left"
          >
            <Clock size={24} className="text-blue-600" />
            <div>
              <p className="font-medium font-body text-slate-900">View All Complaints</p>
              <p className="text-xs text-slate-500 font-body">Manage complaint tickets</p>
            </div>
          </button>
          {['admin', 'manager'].includes(user?.role) && (
            <button
              onClick={() => navigate('/complaints/problem-types')}
              data-testid="quick-action-problem-types"
              className="flex items-center gap-3 p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-left"
            >
              <AlertTriangle size={24} className="text-slate-600" />
              <div>
                <p className="font-medium font-body text-slate-900">Problem Types</p>
                <p className="text-xs text-slate-500 font-body">Manage complaint categories</p>
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComplaintsDashboard;
